import { useEffect, useState } from 'react';
import { Modal, Spinner } from '../../../components/common';
import Header from '../../../components/common/global-top-bar';
import RichTextEditor from '../../../components/rich-text-editor/rich-text-editor';
import { ChevronDownIcon, ChevronRightIcon } from '../../../components/icons';
import {
  CreateVersionedRecordInput,
  VersionedRecordKey,
  VersionedRecordResponse,
} from '../../../hooks/useVersionedRecords';
import { useCreateVersionedRecord } from '../../../hooks/useVersionedRecords';
import { useVersionedRecord } from '../../../hooks/useVersionedRecords';
import { TextBlocksHeaderOptions } from '../common/text-blocks-constants';
import SwitchBar from '../../../components/switch-bar';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

type MemberDomainFamily = 'kindly_human' | 'kindly_christian';

const memberFamilyToKeys = (family: MemberDomainFamily) =>
  family === 'kindly_christian'
    ? {
        terms: VersionedRecordKey.KINDLY_CHRISTIAN_MEMBER_TERMS_OF_SERVICE,
        privacy: VersionedRecordKey.KINDLY_CHRISTIAN_MEMBER_PRIVACY_POLICY,
      }
    : {
        terms: VersionedRecordKey.MEMBER_TERMS_OF_SERVICE,
        privacy: VersionedRecordKey.MEMBER_PRIVACY_POLICY,
      };

const MemberAgreementsWithDomainSelector: React.FC = () => {
  const [memberFamily, setMemberFamily] = useState<MemberDomainFamily>('kindly_human');
  const { terms: memberTermsKey, privacy: memberPrivacyKey } = memberFamilyToKeys(memberFamily);
  const { data: memberTosData, isLoading: memberTosLoading } = useVersionedRecord(memberTermsKey);
  const { createVersionedRecord: memberCreateTos } = useCreateVersionedRecord(memberTermsKey);
  const { data: memberPrivacyData, isLoading: memberPrivacyLoading } = useVersionedRecord(memberPrivacyKey);
  const { createVersionedRecord: memberCreatePrivacy } = useCreateVersionedRecord(memberPrivacyKey);

  return (
    <div className="mb-6">
      <div className="flex w-full flex-wrap items-center justify-start gap-8 rounded-t bg-gray-50 p-4 hover:bg-gray-100">
        <h2 className="text-lg font-semibold">Member Agreements</h2>
        <div className="flex items-center gap-2">
          <label htmlFor="member-domain-family" className="text-sm text-gray-600">
            Member domain family
          </label>
          <select
            id="member-domain-family"
            className="min-w-[16rem] rounded border border-gray-300 bg-white py-1.5 pl-3 pr-9 text-sm shadow-sm"
            value={memberFamily}
            onChange={(e) => setMemberFamily(e.target.value as MemberDomainFamily)}
          >
            <option value="kindly_human">Kindly Human (incl. restricted)</option>
            <option value="kindly_christian">Kindly Christian (incl. restricted)</option>
          </select>
        </div>
      </div>
      <div className="rounded-b border-x border-b border-t-0 p-4">
        <UserAgreementSection
          key={memberFamily}
          title=""
          isLoading={memberTosLoading || memberPrivacyLoading}
          tosKey={memberTermsKey}
          tosData={memberTosData}
          createTos={memberCreateTos}
          privacyKey={memberPrivacyKey}
          privacyData={memberPrivacyData}
          createPrivacy={memberCreatePrivacy}
          hideOuterHeader
        />
      </div>
    </div>
  );
};

const VersionedAgreements: React.FC = () => {
  //client admin agreements
  const { data: clientAdminTosData, isLoading: clientAdminTosLoading } = useVersionedRecord(
    VersionedRecordKey.CLIENT_ADMIN_TERMS_OF_SERVICE,
  );
  const { createVersionedRecord: clientAdminCreateTos } = useCreateVersionedRecord(
    VersionedRecordKey.CLIENT_ADMIN_TERMS_OF_SERVICE,
  );
  const { data: clientAdminPrivacyData, isLoading: clientAdminPrivacyLoading } = useVersionedRecord(
    VersionedRecordKey.CLIENT_ADMIN_PRIVACY_POLICY,
  );
  const { createVersionedRecord: clientAdminCreatePrivacy } = useCreateVersionedRecord(
    VersionedRecordKey.CLIENT_ADMIN_PRIVACY_POLICY,
  );
  //peer agreements
  const { data: peerTosData, isLoading: peerTosLoading } = useVersionedRecord(VersionedRecordKey.PEER_TERMS_OF_SERVICE);
  const { createVersionedRecord: peerCreateTos } = useCreateVersionedRecord(VersionedRecordKey.PEER_TERMS_OF_SERVICE);
  const { data: peerPrivacyData, isLoading: peerPrivacyLoading } = useVersionedRecord(
    VersionedRecordKey.PEER_PRIVACY_POLICY,
  );
  const { createVersionedRecord: peerCreatePrivacy } = useCreateVersionedRecord(VersionedRecordKey.PEER_PRIVACY_POLICY);
  //peersona agreements
  const { data: peersonaTosData, isLoading: peersonaTosLoading } = useVersionedRecord(
    VersionedRecordKey.PEERSONA_ACKNOWLEDGEMENT,
  );
  const { createVersionedRecord: peersonaCreateTos } = useCreateVersionedRecord(
    VersionedRecordKey.PEERSONA_ACKNOWLEDGEMENT,
  );
  return (
    <div>
      <Header heading="Agreement Management" />
      <SwitchBar heading={TextBlocksHeaderOptions} position={3} />
      <div className="max-window-height overflow-y-auto pb-32 pt-4">
        <div className="w-full px-7">
          <MemberAgreementsWithDomainSelector />
          <UserAgreementSection
            title="Client Administrator Agreements"
            isLoading={clientAdminTosLoading || clientAdminPrivacyLoading}
            tosKey={VersionedRecordKey.CLIENT_ADMIN_TERMS_OF_SERVICE}
            tosData={clientAdminTosData}
            createTos={clientAdminCreateTos}
            privacyKey={VersionedRecordKey.CLIENT_ADMIN_PRIVACY_POLICY}
            privacyData={clientAdminPrivacyData}
            createPrivacy={clientAdminCreatePrivacy}
          />
          <UserAgreementSection
            title="Peer Agreements"
            isLoading={peerTosLoading || peerPrivacyLoading}
            tosKey={VersionedRecordKey.PEER_TERMS_OF_SERVICE}
            tosData={peerTosData}
            createTos={peerCreateTos}
            privacyKey={VersionedRecordKey.PEER_PRIVACY_POLICY}
            privacyData={peerPrivacyData}
            createPrivacy={peerCreatePrivacy}
          />
          <UserAgreementSection
            title="Peersona Agreements"
            isLoading={peersonaTosLoading}
            tosKey={VersionedRecordKey.PEERSONA_ACKNOWLEDGEMENT}
            tosData={peersonaTosData}
            createTos={peersonaCreateTos}
          />
        </div>
      </div>
    </div>
  );
};

interface UserAgreementSectionProps {
  title: string;
  isLoading: boolean;
  tosKey: VersionedRecordKey;
  tosData: VersionedRecordResponse | undefined;
  createTos: UseMutationResult<AxiosResponse<any, any>, unknown, CreateVersionedRecordInput, unknown>;
  privacyKey?: VersionedRecordKey;
  privacyData?: VersionedRecordResponse | undefined;
  createPrivacy?: UseMutationResult<AxiosResponse<any, any>, unknown, CreateVersionedRecordInput, unknown>;
  /** When true, skip the gray section header (parent provides its own) */
  hideOuterHeader?: boolean;
}

const UserAgreementSection: React.FC<UserAgreementSectionProps> = ({
  title,
  isLoading,
  tosKey,
  tosData,
  createTos,
  privacyKey,
  privacyData,
  createPrivacy,
  hideOuterHeader = false,
}) => {
  const [tosExpanded, setTosExpanded] = useState(false);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);
  const [tosContent, setTosContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [isDirty, setIsDirty] = useState({ tos: false, privacy: false });
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);

  useEffect(() => {
    if (tosData) setTosContent(tosData.text);
    if (privacyData) setPrivacyContent(privacyData.text);
  }, [tosData, privacyData]);

  useEffect(() => {
    if (confirmUpdate) {
      setConfirmSave(false);
      handleSave(isDirty.tos ? 'tos' : 'privacy');
    }
  }, [confirmUpdate]);

  const handleSave = async (type: 'tos' | 'privacy') => {
    if (!confirmSave && !confirmUpdate) {
      setConfirmSave(true);
      return;
    }
    const createRecord = type === 'tos' ? createTos : createPrivacy;
    if (createRecord) {
      await createRecord.mutateAsync({ text: type === 'tos' ? tosContent : privacyContent });
      setIsDirty((prev) => ({ ...prev, [type]: false }));
    }
    setConfirmUpdate(false);
  };

  const handleChange = (type: 'tos' | 'privacy', value: string) => {
    if (type === 'tos') setTosContent(value);
    if (type === 'privacy') setPrivacyContent(value);
    setIsDirty((prev) => ({ ...prev, [type]: true }));
  };

  return (
    <div className={hideOuterHeader ? '' : 'mb-6'}>
      {!hideOuterHeader && (
        <div className="flex w-full items-center justify-between rounded-t bg-gray-50 p-4 hover:bg-gray-100">
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
      )}

      <div className={hideOuterHeader ? '' : 'rounded-b border-x border-b p-4'}>
        {isLoading && <Spinner />}
        {tosData && (
          <div className="mb-8">
            <button
              className="flex w-full items-center justify-between p-3 hover:bg-gray-50"
              onClick={() => setTosExpanded(!tosExpanded)}
            >
              <div className="flex items-center">
                {tosExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
                <span className="ml-2 font-medium">Terms of Service</span>
              </div>
              <button
                className="stacked-bar-blue-400 h-6 rounded-full px-2 text-center text-sm text-white disabled:bg-gray-400"
                onClick={() => handleSave('tos')}
                disabled={!isDirty.tos || tosContent.length === 0}
              >
                SAVE
              </button>
            </button>
            {tosExpanded && (
              <div className="mt-4">
                <RichTextEditor
                  content={tosContent}
                  onChange={(value) => handleChange('tos', value)}
                  key={`${tosKey}-editor`}
                />
              </div>
            )}
          </div>
        )}
        {privacyData && (
          <div className="mb-8">
            <button
              className="flex w-full items-center justify-between p-3 hover:bg-gray-50"
              onClick={() => setPrivacyExpanded(!privacyExpanded)}
            >
              <div className="flex items-center">
                {privacyExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
                <span className="ml-2 font-medium">Privacy Policy</span>
              </div>
              <button
                className="stacked-bar-blue-400 h-6 rounded-full px-2 text-center text-sm text-white disabled:bg-gray-400"
                onClick={() => handleSave('privacy')}
                disabled={!isDirty.privacy || privacyContent.length === 0}
              >
                SAVE
              </button>
            </button>
            {privacyExpanded && (
              <div className="mt-4">
                <RichTextEditor
                  content={privacyContent}
                  onChange={(value) => handleChange('privacy', value)}
                  key={`${privacyKey}-editor`}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <Modal
        isModel={confirmSave}
        InnerComponent={
          <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h2 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                    {`Update Versioned Record for ${isDirty.tos ? 'Terms of Service' : 'Privacy Policy'}`}?
                  </h2>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Select confirm to create your new versioned record.</p>
                    <p className="text-sm text-gray-500">Select cancel to discard your changes.</p>
                  </div>
                </div>
              </div>
              <div className="justify-start py-3 pl-0 sm:pl-4">
                <button
                  type="button"
                  className="stacked-bar-blue-400 mr-0 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:mr-4 sm:w-auto sm:text-sm"
                  data-testid="remove-scheduled-eligibility-button"
                  onClick={() => {
                    setConfirmUpdate(true);
                  }}
                >
                  UPDATE
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                  data-testid="cancel-delete-scheduled-eligibility-button"
                  onClick={() => setConfirmSave(false)}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default VersionedAgreements;
